import { CSSProperties, RefObject, useRef } from 'react';

import { asRect, getArea, reverseTranslate } from '../../../common/maths/geometry/rect';
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';
import { HIDDEN_POSITION } from './tooltipConstants';
import {
    getArrowPosition,
    getShouldDisplayArrow,
    getTooltipInsetsForBounds,
    getTooltipPosition,
    isTooltipTargetVisible,
} from './tooltipUtil';
import { TooltipOptions } from './tooltopTypes';

const EMPTY_RECT: Rect = {
    x: 0,
    y: 0,
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    width: 0,
    height: 0,
};

const HIDDEN_RECT: Rect = {
    ...EMPTY_RECT,
    top: HIDDEN_POSITION.top,
    left: HIDDEN_POSITION.left,
    x: HIDDEN_POSITION.left,
    y: HIDDEN_POSITION.top,
};

/**
 * Reads a panel's current bounds for placement calculations.
 */
const getPanelRect = (panelRef: RefObject<HTMLElement>): Rect =>
    panelRef.current?.getBoundingClientRect() || EMPTY_RECT;

/**
 * Reads the arrow bounds for arrow placement calculations.
 */
const getArrowRect = (arrowRef: RefObject<HTMLElement>): Rect =>
    arrowRef.current?.getBoundingClientRect() || EMPTY_RECT;

/**
 * Converts the target bounds into the coordinate system of its relative parent.
 */
const getPanelTargetRect = (targetRect: Rect, relativeParentElement?: HTMLElement | null): Rect => {
    const relativeParentRect = relativeParentElement?.getBoundingClientRect();
    if (!relativeParentRect) return targetRect;

    return reverseTranslate({ x: relativeParentRect.x, y: relativeParentRect.y }, targetRect);
};

/**
 * Resolves the coordinate space that CSS uses for a floating panel's top and left.
 */
export const getFloatingPanelRelativeParent = (
    panel: HTMLElement,
    relativeParentSelector?: string,
): HTMLElement | null => {
    if (relativeParentSelector) {
        const selectedParent = document.querySelector<HTMLElement>(relativeParentSelector);
        if (selectedParent) return selectedParent;
    }

    return panel.offsetParent instanceof HTMLElement ? panel.offsetParent : null;
};

/**
 * Gets the canvas space that remains after mobile chrome overlays its edges.
 */
const getCanvasTooltipBounds = (canvasElement: HTMLElement): Rect => {
    const canvasRect = canvasElement.getBoundingClientRect();
    const mobileHeaderRect = document.querySelector<HTMLElement>('.MobileHeader')?.getBoundingClientRect();
    const mobileToolbarRect = document.querySelector<HTMLElement>('#toolbar.MobileToolbar')?.getBoundingClientRect();

    const headerOverlapsCanvas =
        mobileHeaderRect &&
        getArea(mobileHeaderRect) > 0 &&
        mobileHeaderRect.bottom > canvasRect.top &&
        mobileHeaderRect.top <= canvasRect.top;
    const toolbarOverlapsCanvas =
        mobileToolbarRect &&
        getArea(mobileToolbarRect) > 0 &&
        mobileToolbarRect.top < canvasRect.bottom &&
        mobileToolbarRect.bottom >= canvasRect.bottom;

    const top = headerOverlapsCanvas ? Math.max(canvasRect.top, mobileHeaderRect.bottom) : canvasRect.top;
    const bottom = toolbarOverlapsCanvas ? Math.min(canvasRect.bottom, mobileToolbarRect.top) : canvasRect.bottom;

    return asRect({ left: canvasRect.left, top, width: canvasRect.width, height: Math.max(0, bottom - top) });
};

type FloatingPanelPositionProps = TooltipOptions & {
    arrowStyle?: CSSProperties;
    pollPosition?: boolean;
    relativeParentSelector?: string;
    selector?: string;
    targetRect?: DOMRect;
};

type UseFloatingPanelPositionArgs = {
    arrowRef: RefObject<HTMLDivElement>;
    insets: Rect;
    panelRef: RefObject<HTMLDivElement>;
    props: FloatingPanelPositionProps;
};

/**
 * Keeps the DOM measurement and positioning work out of the panel renderer.
 */
export const useFloatingPanelPosition = ({ arrowRef, insets, panelRef, props }: UseFloatingPanelPositionArgs) => {
    const targetElementRef = useRef<HTMLElement | null>(null);
    const canvasTooltipBoundsRef = useRef<Rect | undefined>(undefined);
    const requestAnimationFrameRef = useRef<number | null>(null);

    /**
     * Sets panel placement and hides panels that have lost their visible anchor.
     */
    const updatePosition = (panelRect: Rect, panelTargetRect: Rect, targetRect: Rect): Rect => {
        const panel = panelRef.current;
        if (!panel) return HIDDEN_RECT;

        const canvasTooltipBounds = canvasTooltipBoundsRef.current;
        const tooltipInsets = canvasTooltipBounds ? getTooltipInsetsForBounds(canvasTooltipBounds, insets) : insets;
        const targetIsVisible = isTooltipTargetVisible(
            targetRect,
            props.position,
            getShouldDisplayArrow(props),
            tooltipInsets,
            canvasTooltipBounds,
        );
        if (!targetIsVisible) {
            panel.style.top = `${HIDDEN_POSITION.top}px`;
            panel.style.left = `${HIDDEN_POSITION.left}px`;
            return HIDDEN_RECT;
        }

        const position = getTooltipPosition(panelTargetRect, panelRect, props);
        panel.style.top = `${position.top}px`;
        panel.style.left = `${position.left}px`;

        // FIXME - WEB-12496 - Avoid measuring window dimensions during a requestAnimationFrame.
        const availableHeight = window.innerHeight - tooltipInsets.top - tooltipInsets.bottom;
        const availableWidth = window.innerWidth - tooltipInsets.left - tooltipInsets.right;
        panel.style.setProperty('--top-position', `${position.top}px`);
        panel.style.setProperty('--left-position', `${position.left}px`);
        panel.style.setProperty('--popup-available-height', `${availableHeight}px`);
        panel.style.setProperty('--popup-available-width', `${availableWidth}px`);

        return position;
    };

    /**
     * Keeps the arrow aligned with the panel's current placement.
     */
    const updateArrow = (panelRect: Rect, arrowRect: Rect, panelTargetRect: Rect) => {
        const arrow = arrowRef.current;
        if (!arrow) return;

        if (getArea(panelRect) === 0 || !getShouldDisplayArrow(props)) {
            arrow.style.visibility = 'hidden';
            return;
        }

        const arrowPosition = getArrowPosition(panelTargetRect, panelRect, arrowRect, props);
        arrow.style.top = `${arrowPosition.top}px`;
        arrow.style.left = `${arrowPosition.left}px`;
        arrow.style.transform = `rotate(${arrowPosition.rotate}deg)`;

        if (props.arrowStyle?.transform) {
            arrow.style.transform = `${arrow.style.transform} ${props.arrowStyle.transform}`;
        }

        arrow.style.visibility = 'visible';
        panelRef.current?.classList.add(arrowPosition.side);
    };

    /**
     * Measures the current target and schedules another pass when requested.
     */
    const updatePanel = (currentSelector: string | undefined) => {
        if (!panelRef.current) {
            requestAnimationFrame(() => updatePanel(currentSelector));
            return;
        }

        const targetElement = props.selector ? document.querySelector<HTMLElement>(props.selector) : null;
        const relativeParentElement = getFloatingPanelRelativeParent(panelRef.current, props.relativeParentSelector);
        const targetRect = props.targetRect || targetElement?.getBoundingClientRect() || EMPTY_RECT;

        if (targetElement !== targetElementRef.current) {
            targetElementRef.current = targetElement;
            const canvasViewport = targetElement?.closest<HTMLElement>('#canvas-viewport') || null;
            canvasTooltipBoundsRef.current = canvasViewport ? getCanvasTooltipBounds(canvasViewport) : undefined;
        }

        const panelRect = getPanelRect(panelRef);
        const arrowRect = getArrowRect(arrowRef);
        const panelTargetRect = getPanelTargetRect(targetRect, relativeParentElement);
        const updatedPanelRect = updatePosition(panelRect, panelTargetRect, targetRect);
        updateArrow(updatedPanelRect, arrowRect, panelTargetRect);

        if (props.pollPosition) {
            requestAnimationFrameRef.current = requestAnimationFrame(() => updatePanel(currentSelector));
        }
    };

    /**
     * Cancels the active polling frame when the panel stops observing its target.
     */
    const cancelUpdatePanel = () => {
        if (requestAnimationFrameRef.current) cancelAnimationFrame(requestAnimationFrameRef.current);
    };

    return { cancelUpdatePanel, updatePanel };
};
